import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getSupplierReturn } from '../../lib/api';
import { SUPPLIER_RETURN_CREDIT_KIND_LABELS, SUPPLIER_RETURN_REASON_LABELS } from '@mlm/shared';

/**
 * Printable Return-to-Supplier (RTS-) slip — the goods-outward document the
 * courier/supplier signs. Opens in a new window from the returns table,
 * auto-fires the browser print dialog, and uses minimal black-on-white styles
 * like the branch batch slip. The root MUST carry `billing-doc`: the print
 * stylesheet (index.css) hides `body *` and only whitelists that class, so
 * without it the sheet comes out blank.
 */
export function SupplierReturnPrintPage() {
  const { id } = useParams<{ id: string }>();
  const q = useQuery({
    queryKey: ['supplier-return-print', id],
    queryFn: () => getSupplierReturn(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (!q.data) return;
    const prev = document.title;
    document.title = 'Return to Supplier';
    const t = window.setTimeout(() => window.print(), 250);
    return () => {
      window.clearTimeout(t);
      document.title = prev;
    };
  }, [q.data]);

  if (q.isLoading) return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  if (!q.data) return <p className="p-6 text-sm text-destructive">Return not found.</p>;

  const r = q.data;
  const isVoid = r.status === 'void';
  const reasonLabel =
    SUPPLIER_RETURN_REASON_LABELS[r.reason as keyof typeof SUPPLIER_RETURN_REASON_LABELS] ??
    r.reason;
  const returned = new Date(r.returnedAt);
  const peso = (v: string | number | null | undefined) => {
    const n = Number(v ?? 0);
    return `₱${(Number.isFinite(n) ? n : 0).toLocaleString('en-PH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <div className="billing-doc relative bg-white text-black mx-auto max-w-2xl p-8 print:p-6 font-[ui-sans-serif] text-[13px] leading-snug">
      {isVoid && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          <span className="-rotate-12 rounded-md border-4 border-red-600 px-10 py-2 text-6xl font-bold tracking-[0.3em] text-red-600 opacity-30">
            VOID
          </span>
        </div>
      )}

      <header className="flex items-start justify-between border-b border-black pb-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-neutral-500">
            Return to supplier
          </div>
          <div className="text-lg font-semibold mt-0.5">{r.supplierName}</div>
          {r.supplierRef && (
            <div className="text-[11px] text-neutral-600 font-mono">Ref {r.supplierRef}</div>
          )}
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-wider text-neutral-500">RTS number</div>
          <div className="text-lg font-mono font-semibold mt-0.5">{r.docNo}</div>
          <div className="text-[11px] text-neutral-600 mt-1">
            {returned.toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'short',
              day: '2-digit',
            })}
          </div>
        </div>
      </header>

      <section className="grid grid-cols-2 gap-4 mt-4 text-[12px]">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-neutral-500">
            Stock leaves branch
          </div>
          <div className="mt-0.5">{r.branchName}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-neutral-500">Reason</div>
          <div className="mt-0.5">{reasonLabel}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-neutral-500">
            Purchase order
          </div>
          <div className="mt-0.5 font-mono">{r.poNumber ?? '—'}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-neutral-500">
            {SUPPLIER_RETURN_CREDIT_KIND_LABELS[r.creditKind] ?? r.creditKind}
          </div>
          <div className="mt-0.5 font-mono">{r.invoiceNo ?? '—'}</div>
        </div>
        {r.memo && (
          <div className="col-span-2">
            <div className="text-[11px] uppercase tracking-wider text-neutral-500">Memo</div>
            <div className="mt-0.5">{r.memo}</div>
          </div>
        )}
        {isVoid && (
          <div className="col-span-2">
            <div className="text-[11px] uppercase tracking-wider text-red-600">Voided</div>
            <div className="mt-0.5">
              {r.voidedAt ? new Date(r.voidedAt).toLocaleString() : ''}
              {r.voidReason ? ` — ${r.voidReason}` : ''}
            </div>
          </div>
        )}
      </section>

      <table className="w-full mt-5 text-[12px] border-t border-b border-black">
        <thead>
          <tr className="border-b border-neutral-400">
            <th className="text-left py-1.5 pr-2 font-medium">SKU</th>
            <th className="text-left py-1.5 pr-2 font-medium">Product</th>
            <th className="text-right py-1.5 pr-2 font-medium">Qty</th>
            <th className="text-right py-1.5 pr-2 font-medium">Unit cost</th>
            <th className="text-right py-1.5 font-medium">Amount</th>
          </tr>
        </thead>
        <tbody>
          {r.lines.map((l) => (
            <tr key={l.id} className="border-b border-neutral-200 last:border-0">
              <td className="py-1.5 pr-2 font-mono text-[11px]">{l.sku}</td>
              <td className="py-1.5 pr-2">
                {l.productName}
                {l.variantName && <span className="text-neutral-500"> — {l.variantName}</span>}
              </td>
              <td className="py-1.5 pr-2 text-right tabular-nums">{l.qty}</td>
              <td className="py-1.5 pr-2 text-right tabular-nums">{peso(l.unitCost)}</td>
              <td className="py-1.5 text-right tabular-nums">{peso(l.lineTotal)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} />
            <td className="py-1.5 text-right text-neutral-600">Subtotal</td>
            <td className="py-1.5 text-right tabular-nums">{peso(r.subtotal)}</td>
          </tr>
          {Number(r.vatAmount) > 0 && (
            <tr>
              <td colSpan={3} />
              <td className="py-1.5 text-right text-neutral-600">VAT</td>
              <td className="py-1.5 text-right tabular-nums">{peso(r.vatAmount)}</td>
            </tr>
          )}
          <tr className="border-t border-neutral-400">
            <td colSpan={3} />
            <td className="py-2 text-right font-medium">Total</td>
            <td className="py-2 text-right font-semibold tabular-nums">{peso(r.total)}</td>
          </tr>
        </tfoot>
      </table>

      <section className="grid grid-cols-2 gap-8 mt-12 text-[11px]">
        <div>
          <div className="border-t border-black pt-1">Released by</div>
          <div className="mt-0.5 text-neutral-600">{r.createdByName ?? ''}</div>
        </div>
        <div>
          <div className="border-t border-black pt-1">Received by (supplier)</div>
          <div className="mt-0.5 text-neutral-600">&nbsp;</div>
        </div>
      </section>

      <div className="mt-6 flex justify-end print:hidden">
        <button
          type="button"
          onClick={() => window.print()}
          className="text-sm px-3 py-1.5 rounded-md border bg-white hover:bg-neutral-50"
        >
          Print again
        </button>
      </div>
    </div>
  );
}
